import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { CountryAddressSchema, PlaceFormatContext, PlaceLike } from './placeFormat';
import { formatPlace, resolvePlaceTemplate } from './placeFormat';
import type { PlacePickerLabels } from './PlacePicker';

/** A place as the picker needs it. */
export interface PlaceOption extends PlaceLike {
  /** The one-line label the server wrote ("Zeytinburnu, İstanbul, Türkiye"). */
  label?: string;
  /** What the country calls this level ("İlçe"). */
  levelName?: string | null;
  hasChildren?: boolean;
  source?: string;
}

export interface PlaceSearchOptions {
  types?: string[];
  country?: string;
  within?: string;
  limit?: number;
  signal?: AbortSignal;
}

/** Where places come from: the app's `/api/v1/geo` client (cached), or an in-memory one in the playground. */
export interface PlaceSource {
  search(query: string, options?: PlaceSearchOptions): Promise<PlaceOption[]>;
  get(id: string, signal?: AbortSignal): Promise<PlaceOption | null>;
  children(id: string, options?: { types?: string[]; signal?: AbortSignal }): Promise<PlaceOption[]>;
  /** Free text → the best matching place (and its chain) for `allowAddress`. */
  resolve?(text: string, options?: { country?: string; signal?: AbortSignal }): Promise<{ place: PlaceOption | null; chain: PlaceOption[]; unmatched: string[] }>;
  /** Tells the server the viewer picked it (recent / frequent boosts). */
  used?(id: string): void;
  peek?(id: string): PlaceOption | undefined;
}

export interface PlaceFormats {
  /** Resolved templates (user › role › party › tenant › platform); empty = the country's default. */
  templates?: Partial<Record<PlaceFormatContext, string>>;
  /** Each country's address schema (levels, defaults). */
  schemaFor?: (countryCode: string | null | undefined) => CountryAddressSchema | undefined;
  lang?: string;
  /** Translated picker strings (the app's i18n); English defaults otherwise. */
  labels?: Partial<PlacePickerLabels>;
}

interface PlaceContextValue {
  source: PlaceSource | null;
  formats: PlaceFormats;
}

const PlaceContext = createContext<PlaceContextValue>({ source: null, formats: {} });

export function PlaceProvider({ source, formats = {}, children }: { source: PlaceSource; formats?: PlaceFormats; children: ReactNode }) {
  return <PlaceContext.Provider value={{ source, formats }}>{children}</PlaceContext.Provider>;
}

export function usePlaceSource(): PlaceSource | null {
  return useContext(PlaceContext).source;
}

export function usePlaceFormats(): PlaceFormats {
  return useContext(PlaceContext).formats;
}

/** Writes a place for a context with the formats in scope (configuration › country default › fallback). */
export function usePlaceFormatter(): (place: PlaceLike, context: PlaceFormatContext, override?: string) => string {
  const formats = usePlaceFormats();
  return useCallback(
    (place: PlaceLike, context: PlaceFormatContext, override?: string) => {
      const template = override ?? resolvePlaceTemplate(context, formats.templates, formats.schemaFor?.(place.countryCode));
      return formatPlace(place, template);
    },
    [formats],
  );
}

/** One place by id: peek first (the source's cache), then a request. `undefined` while loading. */
export function usePlace(id: string | null | undefined): { place: PlaceOption | null | undefined; loading: boolean } {
  const source = usePlaceSource();
  const peeked = id && source?.peek ? source.peek(id) : undefined;
  const [state, setState] = useState<{ id: string; place: PlaceOption | null } | null>(null);
  useEffect(() => {
    if (!id || !source || peeked) return;
    const controller = new AbortController();
    source
      .get(id, controller.signal)
      .then((place) => setState({ id, place }))
      .catch(() => {
        if (!controller.signal.aborted) setState({ id, place: null });
      });
    return () => controller.abort();
  }, [id, source, peeked]);
  if (!id) return { place: null, loading: false };
  if (peeked) return { place: peeked, loading: false };
  if (state?.id === id) return { place: state.place, loading: false };
  return { place: undefined, loading: !!source };
}
